import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSuperAdminFetch, Account } from "@/contexts/SuperAdminContext";
import {
  ScrollText, Activity, ArrowLeft, Search, ChevronLeft, ChevronRight, Loader2, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  type PlatformAuditLogEntry,
  type SuperadminPlatformAuditPage,
  type FieldAuditLogEntry,
  type FieldAuditLogPage,
} from "@/lib/api";
import { format, formatDistanceToNow, startOfToday, subDays, startOfMonth } from "date-fns";

// ── Shared label/colour maps (dark theme) ──────────────────────────────────────
const ACTION_LABELS: Record<string, string> = {
  POST: "Create", PUT: "Update", PATCH: "Patch", DELETE: "Delete", WIDGET: "Viewed",
};
const ALL_ACTIONS = ["POST", "PUT", "PATCH", "DELETE", "WIDGET"];

const MODULE_COLORS: Record<string, string> = {
  "Authentication":     "bg-purple-500/15 text-purple-300",
  "Employees":          "bg-blue-500/15 text-blue-300",
  "Leave":              "bg-teal-500/15 text-teal-300",
  "Documents":          "bg-indigo-500/15 text-indigo-300",
  "Payroll":            "bg-green-500/15 text-green-300",
  "Salary Config":      "bg-lime-500/15 text-lime-300",
  "Performance":        "bg-orange-500/15 text-orange-300",
  "Surveys":            "bg-pink-500/15 text-pink-300",
  "Workflows":          "bg-violet-500/15 text-violet-300",
  "Onboarding":         "bg-cyan-500/15 text-cyan-300",
  "Offboarding":        "bg-rose-500/15 text-rose-300",
  "Organization":       "bg-sky-500/15 text-sky-300",
  "Teams":              "bg-emerald-500/15 text-emerald-300",
  "Raise Concern":      "bg-red-500/15 text-red-300",
  "Settings":           "bg-gray-500/15 text-gray-300",
  "Field Permissions":  "bg-yellow-500/15 text-yellow-300",
  "Nav Permissions":    "bg-yellow-500/15 text-yellow-300",
  "Corporate Calendar": "bg-blue-500/15 text-blue-300",
  "Expenses":           "bg-amber-500/15 text-amber-300",
  "Medical Checks":     "bg-fuchsia-500/15 text-fuchsia-300",
  "Certifications":     "bg-emerald-500/15 text-emerald-300",
  "Equipment":          "bg-stone-500/15 text-stone-300",
  "Recruitment":        "bg-indigo-500/15 text-indigo-300",
  "Document Templates": "bg-violet-500/15 text-violet-300",
  "GDPR":               "bg-red-500/15 text-red-300",
};
const moduleColor = (m: string) => MODULE_COLORS[m] ?? "bg-white/10 text-white/60";

const ACTION_BADGE: Record<string, string> = {
  POST:   "bg-emerald-500/15 text-emerald-400",
  PUT:    "bg-blue-500/15 text-blue-400",
  PATCH:  "bg-amber-500/15 text-amber-400",
  DELETE: "bg-red-500/15 text-red-400",
  WIDGET: "bg-white/5 text-white/40",
};

// ── Date presets ────────────────────────────────────────────────────────────────
const fmt = (d: Date) => format(d, "yyyy-MM-dd");
const DATE_PRESETS: { id: string; label: string; range: () => { from: string; to: string } }[] = [
  { id: "today",     label: "Today",      range: () => ({ from: fmt(startOfToday()),           to: fmt(new Date()) }) },
  { id: "last7",     label: "7d",         range: () => ({ from: fmt(subDays(new Date(), 6)),   to: fmt(new Date()) }) },
  { id: "last30",    label: "30d",        range: () => ({ from: fmt(subDays(new Date(), 29)),  to: fmt(new Date()) }) },
  { id: "thisMonth", label: "This month", range: () => ({ from: fmt(startOfMonth(new Date())), to: fmt(new Date()) }) },
  { id: "all",       label: "All",        range: () => ({ from: "", to: "" }) },
];

const LIMIT = 50;

export default function AccountAuditLog() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiFetch = useSuperAdminFetch();

  const [account, setAccount] = useState<Account | null>(null);
  const [tab, setTab] = useState<"platform" | "field">("platform");

  // Load account (for header)
  useEffect(() => {
    if (!id) return;
    apiFetch<Account>(`/api/superadmin/accounts/${id}`)
      .then(setAccount)
      .catch(() => setAccount(null));
  }, [id, apiFetch]);

  return (
    <div className="min-h-screen p-6 text-[#e6edf3]">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button
          variant="ghost" size="icon"
          onClick={() => navigate("/superadmin/accounts")}
          className="h-8 w-8 text-white/40 hover:text-white hover:bg-white/5"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <ScrollText className="h-5 w-5 text-blue-400" />
            Audit Log{account ? ` — ${account.name}` : ""}
          </h1>
          <p className="text-xs text-white/40 mt-0.5">
            Platform activity and employee field-level change history for this tenant.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4 border-b border-white/[0.06]">
        <button
          onClick={() => setTab("platform")}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === "platform"
              ? "border-blue-400 text-white"
              : "border-transparent text-white/40 hover:text-white/70"
          }`}
        >
          <Activity className="h-4 w-4" /> Platform Activity
        </button>
        <button
          onClick={() => setTab("field")}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === "field"
              ? "border-blue-400 text-white"
              : "border-transparent text-white/40 hover:text-white/70"
          }`}
        >
          <ScrollText className="h-4 w-4" /> Field Changes
        </button>
      </div>

      {id && (tab === "platform"
        ? <PlatformActivityTab accountId={id} apiFetch={apiFetch} />
        : <FieldChangesTab accountId={id} apiFetch={apiFetch} />)}
    </div>
  );
}

// ── Platform Activity tab ───────────────────────────────────────────────────────
function PlatformActivityTab({
  accountId, apiFetch,
}: {
  accountId: string;
  apiFetch: <T,>(path: string, options?: RequestInit) => Promise<T>;
}) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<PlatformAuditLogEntry[]>([]);
  const [modules, setModules] = useState<string[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [filterModule, setFilterModule] = useState("all");
  const [filterAction, setFilterAction] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [activePreset, setActivePreset] = useState<string | null>("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set("page", String(page));
      qs.set("limit", String(LIMIT));
      if (filterModule !== "all") qs.set("module", filterModule);
      if (filterAction !== "all") qs.set("action", filterAction);
      if (search) qs.set("search", search);
      if (from) qs.set("from", from);
      if (to) qs.set("to", to);
      const data = await apiFetch<SuperadminPlatformAuditPage>(
        `/api/superadmin/accounts/${accountId}/platform-audit-log?${qs}`
      );
      setRows(data.rows);
      setModules(data.modules);
      setTotal(data.total);
    } catch {
      setRows([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [accountId, apiFetch, page, filterModule, filterAction, search, from, to]);

  useEffect(() => { load(); }, [load]);

  const applyPreset = (presetId: string) => {
    const p = DATE_PRESETS.find((x) => x.id === presetId);
    if (!p) return;
    const r = p.range();
    setFrom(r.from); setTo(r.to); setActivePreset(presetId); setPage(1);
  };

  const pages = Math.ceil(total / LIMIT);

  return (
    <>
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-white/30" />
          <Input
            placeholder="Search message or user…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { setSearch(searchInput); setPage(1); } }}
            className="h-8 w-56 pl-8 bg-white/5 border-white/10 text-white placeholder:text-white/25 text-xs"
          />
        </div>
        <Button
          size="sm" variant="outline"
          className="h-8 border-white/10 bg-white/[0.04] text-white/70 hover:text-white text-xs"
          onClick={() => { setSearch(searchInput); setPage(1); }}
        >
          Search
        </Button>

        <Select value={filterModule} onValueChange={(v) => { setFilterModule(v); setPage(1); }}>
          <SelectTrigger className="w-44 h-8 bg-white/5 border-white/10 text-white text-xs">
            <SelectValue placeholder="All modules" />
          </SelectTrigger>
          <SelectContent className="bg-[#1c2128] border-white/10 text-white">
            <SelectItem value="all">All modules</SelectItem>
            {modules.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={filterAction} onValueChange={(v) => { setFilterAction(v); setPage(1); }}>
          <SelectTrigger className="w-36 h-8 bg-white/5 border-white/10 text-white text-xs">
            <SelectValue placeholder="All actions" />
          </SelectTrigger>
          <SelectContent className="bg-[#1c2128] border-white/10 text-white">
            <SelectItem value="all">All actions</SelectItem>
            {ALL_ACTIONS.map((a) => <SelectItem key={a} value={a}>{ACTION_LABELS[a]}</SelectItem>)}
          </SelectContent>
        </Select>

        <div className="inline-flex items-center rounded-lg border border-white/10 bg-white/[0.03] p-0.5 h-8">
          {DATE_PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => applyPreset(p.id)}
              className={`px-2 h-7 text-[11px] font-medium rounded-md transition-colors ${
                activePreset === p.id ? "bg-white/10 text-white" : "text-white/40 hover:text-white/70"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <Button
          size="sm" variant="ghost"
          className="h-8 text-white/40 hover:text-white text-xs gap-1.5"
          onClick={load}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>

        <span className="text-xs text-white/35 ml-auto">{total} records</span>
      </div>

      {/* List */}
      <div className="rounded-lg border border-white/[0.06] bg-[#161b22] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="h-6 w-6 animate-spin text-white/30" />
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-white/30 gap-2">
            <Activity className="h-8 w-8" />
            <p className="text-sm">No activity found</p>
          </div>
        ) : (
          <div className="divide-y divide-white/[0.04]">
            {rows.map((r) => (
              <div key={r.id} className="flex items-start gap-3 px-4 py-3 hover:bg-white/[0.02]">
                <span className={`shrink-0 mt-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded ${ACTION_BADGE[r.action] ?? "bg-white/10 text-white/50"}`}>
                  {ACTION_LABELS[r.action] ?? r.action}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium leading-snug text-[#e6edf3]">{r.message}</p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${moduleColor(r.module)}`}>
                      {r.module}
                    </span>
                    <span className="text-xs text-white/40">
                      {r.userName}{r.userEmail && <span className="text-white/25"> ({r.userEmail})</span>}
                    </span>
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-xs text-white/40 whitespace-nowrap">
                    {formatDistanceToNow(new Date(r.createdAt), { addSuffix: true })}
                  </p>
                  <p className="text-[10px] text-white/25 whitespace-nowrap mt-0.5">
                    {format(new Date(r.createdAt), "dd MMM yyyy HH:mm:ss")}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.06]">
            <span className="text-xs text-white/40">Page {page} of {pages}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1 || loading}
                onClick={() => setPage((p) => p - 1)}
                className="h-7 px-2 border-white/10 bg-white/[0.04] text-white/60">
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="outline" disabled={page >= pages || loading}
                onClick={() => setPage((p) => p + 1)}
                className="h-7 px-2 border-white/10 bg-white/[0.04] text-white/60">
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ── Field Changes tab ───────────────────────────────────────────────────────────
function FieldChangesTab({
  accountId, apiFetch,
}: {
  accountId: string;
  apiFetch: <T,>(path: string, options?: RequestInit) => Promise<T>;
}) {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<FieldAuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filterAction, setFilterAction] = useState("all");
  const [filterSection, setFilterSection] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set("page", String(page));
      qs.set("limit", String(LIMIT));
      if (filterAction !== "all") qs.set("action", filterAction);
      if (filterSection) qs.set("section", filterSection);
      const data = await apiFetch<FieldAuditLogPage>(
        `/api/superadmin/accounts/${accountId}/audit-logs?${qs}`
      );
      setLogs(data.logs);
      setTotal(data.total);
    } catch {
      setLogs([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [accountId, apiFetch, page, filterAction, filterSection]);

  useEffect(() => { load(); }, [load]);

  const pages = Math.ceil(total / LIMIT);

  return (
    <>
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Select value={filterAction} onValueChange={(v) => { setFilterAction(v); setPage(1); }}>
          <SelectTrigger className="w-36 h-8 bg-white/5 border-white/10 text-white text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#1c2128] border-white/10 text-white">
            <SelectItem value="all">All Actions</SelectItem>
            <SelectItem value="update">Update</SelectItem>
            <SelectItem value="create">Create</SelectItem>
            <SelectItem value="delete">Delete</SelectItem>
          </SelectContent>
        </Select>
        <Input
          placeholder="Filter by section…"
          value={filterSection}
          onChange={(e) => setFilterSection(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); load(); } }}
          className="h-8 w-48 bg-white/5 border-white/10 text-white placeholder:text-white/25 text-xs"
        />
        <Button
          size="sm" variant="outline"
          className="h-8 border-white/10 bg-white/[0.04] text-white/70 hover:text-white text-xs"
          onClick={() => { setPage(1); load(); }}
        >
          Apply
        </Button>
        <span className="text-xs text-white/35 ml-auto">{total} records</span>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-white/[0.06] bg-[#161b22] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="h-6 w-6 animate-spin text-white/30" />
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-white/30 gap-2">
            <ScrollText className="h-8 w-8" />
            <p className="text-sm">No field changes found</p>
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-[#161b22] border-b border-white/[0.06]">
              <tr>
                <th className="text-left px-4 py-2.5 text-white/40 font-semibold uppercase tracking-wider">Employee</th>
                <th className="text-left px-4 py-2.5 text-white/40 font-semibold uppercase tracking-wider">Section / Field</th>
                <th className="text-left px-4 py-2.5 text-white/40 font-semibold uppercase tracking-wider">Action</th>
                <th className="text-left px-4 py-2.5 text-white/40 font-semibold uppercase tracking-wider">Old → New</th>
                <th className="text-left px-4 py-2.5 text-white/40 font-semibold uppercase tracking-wider">Changed By</th>
                <th className="text-left px-4 py-2.5 text-white/40 font-semibold uppercase tracking-wider">Date</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                  <td className="px-4 py-2.5 text-[#e6edf3] font-medium">{log.employee_name || `#${log.employee_id}`}</td>
                  <td className="px-4 py-2.5 text-white/60">
                    <div>{log.section}</div>
                    {log.field_name && <div className="text-white/35 font-mono">{log.field_name}</div>}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                      log.action === "create" ? "bg-emerald-500/15 text-emerald-400" :
                      log.action === "delete" ? "bg-red-500/15 text-red-400" :
                      "bg-blue-500/15 text-blue-400"
                    }`}>
                      {log.action}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-white/50 max-w-[200px]">
                    {log.old_value || log.new_value ? (
                      <div className="truncate">
                        <span className="text-red-400/70 line-through">{log.old_value || "—"}</span>
                        {" → "}
                        <span className="text-emerald-400/80">{log.new_value || "—"}</span>
                      </div>
                    ) : <span className="text-white/20">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-white/40 font-mono text-[10px]">{log.changed_by_email || "—"}</td>
                  <td className="px-4 py-2.5 text-white/40 whitespace-nowrap">{new Date(log.changed_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.06]">
            <span className="text-xs text-white/40">Page {page} of {pages}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1 || loading}
                onClick={() => setPage((p) => p - 1)}
                className="h-7 px-2 border-white/10 bg-white/[0.04] text-white/60">
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="outline" disabled={page >= pages || loading}
                onClick={() => setPage((p) => p + 1)}
                className="h-7 px-2 border-white/10 bg-white/[0.04] text-white/60">
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
